/**
 * Rating Drilldown Modal Component (CR-6381)
 *
 * Shows the list of suppliers that fall into a specific rating band.
 * Displayed when clicking a bar in the Rating Distribution chart.
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Loader2, ExternalLink } from 'lucide-react'
import { supplierRatingDistributionAPI, type RatingDrilldownSupplier } from '../../lib/api'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ratingBandMeta } from './PerformanceRatingDistribution'
import type { SupplierRatingThreshold } from '../../lib/api'

interface RatingDrilldownModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Callback to close the modal */
  onClose: () => void
  /** The rating grade to show suppliers for */
  rating: string
  /** Period type: 'monthly' or 'overall' */
  period: 'monthly' | 'overall'
  /** Date range for the query */
  startDate?: string
  endDate?: string
  /** Strategic only filter */
  strategicOnly?: boolean
  /** CR-8237: the Supplier filter the bars were drawn with, so the list matches its bar */
  supplierId?: number
  /** CR-6269: the viewer's Supplier Grade Thresholds, for the title */
  bands?: SupplierRatingThreshold[]
}

export function RatingDrilldownModal({
  open,
  onClose,
  rating,
  period,
  startDate,
  endDate,
  strategicOnly,
  supplierId,
  bands = [],
}: RatingDrilldownModalProps) {
  const navigate = useNavigate()
  const { formatNumber } = useFormatters()
  const [suppliers, setSuppliers] = useState<RatingDrilldownSupplier[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch suppliers when modal opens or parameters change
  useEffect(() => {
    if (!open || !rating) return

    const fetchSuppliers = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await supplierRatingDistributionAPI.getDrilldown(rating, {
          start_date: startDate,
          end_date: endDate,
          strategic_only: strategicOnly,
          supplier_id: supplierId,
          period,
        })
        setSuppliers(response.data.suppliers)
      } catch (err) {
        console.error('[RatingDrilldownModal] Error fetching suppliers:', err)
        setError('Failed to load suppliers. Please try again.')
        setSuppliers([])
      } finally {
        setLoading(false)
      }
    }

    fetchSuppliers()
  }, [open, rating, period, startDate, endDate, strategicOnly, supplierId])

  // Navigate to supplier detail page
  const handleSupplierClick = useCallback(
    (supplierId: number) => {
      navigate(`/suppliers/${supplierId}`)
      onClose()
    },
    [navigate, onClose]
  )

  // Format OTIF percentage
  const formatOTIF = (value: number | null): string => {
    if (value === null || value === undefined) return 'N/A'
    return `${formatNumber(value)}%`
  }

  // CR-6269: the configured grade name, e.g. "Platinum (A)"; the bare grade
  // when the company has no band for it.
  const meta = ratingBandMeta(bands)[rating]
  const ratingLabel = meta ? (rating === 'N/A' ? meta.label : `${meta.label} (${rating})`) : rating
  const periodLabel = period === 'overall' ? 'Year to Date' : 'This Month'

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Suppliers with {ratingLabel} Rating - {periodLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">{error}</div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && suppliers.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No suppliers found with this rating.
            </div>
          )}

          {/* Suppliers Table */}
          {!loading && !error && suppliers.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">PO Lines</TableHead>
                  <TableHead className="text-right">OTIF %</TableHead>
                  <TableHead className="text-right">PPM</TableHead>
                  <TableHead className="text-right">NCRs Raised</TableHead>
                  <TableHead className="text-right">Rating Score</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow
                    key={supplier.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSupplierClick(supplier.id)}
                  >
                    <TableCell className="font-medium">{supplier.name}</TableCell>
                    <TableCell className="text-right">
                      {formatNumber(supplier.po_line_count)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatOTIF(supplier.otif_percent)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(supplier.ppm)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(supplier.ncr_count)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatOTIF(supplier.rating_score)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="View supplier details"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-sm text-muted-foreground">
            {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}
          </span>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
